import type Hls from 'hls.js';
import type { Cam, Quality, TileStatus } from './types';
import { ensureHls, canPlayNativeHls, gridConfig } from './hls';
import { makeVideo, playSafe, snapshotTo } from './video';
import { masterUrl, variantUrl, VARIANT_FILE, LEVEL_IDX } from '@/config/streams';
import { isYoonee, cctvStreamUrl, cctvTokenExpiry, cctvInvalidate } from '@/config/cctv';
import { QUALITY_CYCLE } from '@/config';
import { effectiveQuality, labelOf, markCam, save, state } from '@/store/state';
import { ICON } from '@/lib/icons';
import { $, hms } from '@/lib/dom';
import { pool } from './pool';
import * as bus from '@/lib/bus';

export class Tile {
  cam: Cam;
  el: HTMLElement;
  video: HTMLVideoElement;
  poster: HTMLCanvasElement;
  hls: Hls | null = null;
  native = false;
  attached = false;
  visible = false;
  lastVisible = 0;
  currentUrl = '';
  fps = 0;

  private gen = 0;
  private backoff = 1000;
  private mediaErrs = 0;
  private mfail = 0;
  private stalls = 0;
  private lastT = -1;
  private lastFrames = 0;
  private bk: number | undefined;
  private tokenExp = 0;      // yoonee: epoch-ms the current stream token expires
  private refreshing = false;
  private nativeErrs = 0;    // yoonee native-playback failures (survives reinit)

  constructor(cam: Cam) {
    this.cam = cam;
    this.el = document.createElement('div');
    this.video = makeVideo();
    this.poster = document.createElement('canvas');
    this.build();
  }

  private build(): void {
    const el = this.el;
    el.className = 'tile slot slot--filled entering';
    el.dataset['id'] = this.cam.id;
    el.dataset['cam'] = this.cam.id;
    el.dataset['s'] = 'idle';
    this.video.className = 'tile-video';
    this.poster.className = 'poster';
    this.poster.hidden = true;
    el.append(this.video, this.poster);
    el.insertAdjacentHTML('beforeend', `
      <div class="t-live"><span class="live-dot"></span><span class="lt">LIVE</span></div>
      <div class="t-name mono"></div>
      <button class="t-quality mono" title="คุณภาพ"></button>
      <div class="t-ctrls">
        <button data-a="expand" title="ขยาย">${ICON.expand}</button>
        <button data-a="reload" title="โหลดใหม่">${ICON.reload}</button>
        <button data-a="remove" title="เอาออก">${ICON.x}</button>
      </div>
      <div class="t-msg"><div class="spin"></div><div class="mtxt"></div><div class="msub"></div></div>`);
    $('.t-name', el)!.textContent = labelOf(this.cam);
    el.addEventListener('animationend', () => el.classList.remove('entering'), { once: true });
    for (const b of el.querySelectorAll<HTMLButtonElement>('.t-ctrls button')) {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        const a = b.dataset['a'];
        if (a === 'remove') bus.emit('wall:remove', this.cam.id);
        else if (a === 'reload') this.hardReinit();
        else if (a === 'expand') bus.emit('spotlight', this.cam.id);
      });
    }
    const qEl = $('.t-quality', el) as HTMLButtonElement;
    // YooNee CCTV is single-quality (no ABR ladder) — hide the quality control.
    if (isYoonee(this.cam)) qEl.style.display = 'none';
    else qEl.addEventListener('click', (e) => { e.stopPropagation(); this.cycleQuality(); });
    this.updateQualityLabel();
  }

  private urlForQuality(q: Quality): string {
    return q === 'AUTO' ? masterUrl(this.cam.id) : variantUrl(this.cam.id, VARIANT_FILE[q]);
  }

  updateQualityLabel(): void {
    if (isYoonee(this.cam)) return; // single-quality, control hidden
    const q = effectiveQuality(this.cam.id);
    const el = $('.t-quality', this.el) as HTMLButtonElement;
    el.textContent = q;
    el.classList.toggle('is-override', !!state.quality[this.cam.id]);
  }

  private cycleQuality(): void {
    const cur = effectiveQuality(this.cam.id);
    const next = QUALITY_CYCLE[(QUALITY_CYCLE.indexOf(cur as never) + 1) % QUALITY_CYCLE.length]!;
    if (next === state.globalQuality) delete state.quality[this.cam.id];
    else state.quality[this.cam.id] = next;
    save();
    this.applyQuality(next);
    this.updateQualityLabel();
  }

  /** Change quality on a live tile without a hard black reload where possible. */
  applyQuality(q: Quality): void {
    if (!this.attached || isYoonee(this.cam)) return;
    const url = this.urlForQuality(q);
    if (url !== this.currentUrl) {
      this.currentUrl = url;
      if (this.hls) this.hls.loadSource(url); // keeps last frame painted
      else if (this.native) this.video.src = url;
    }
    this.applyCaps(q);
    this.updateQualityLabel();
  }

  private applyCaps(q: Quality): void {
    if (!this.hls || !this.currentUrl.endsWith('master.m3u8')) return;
    this.hls.autoLevelCapping = q === 'AUTO' ? -1 : LEVEL_IDX[q];
    if (q !== 'AUTO') this.hls.nextLevel = LEVEL_IDX[q];
  }

  async attach(): Promise<void> {
    if (this.attached) return;
    this.attached = true;
    pool.attached.add(this);
    const g = this.gen;
    this.backoff = 1000; this.mediaErrs = 0; this.mfail = 0; this.stalls = 0; this.lastT = -1;
    const q = effectiveQuality(this.cam.id);
    const v = this.video;
    v.muted = true;
    this.poster.hidden = true;
    this.setStatus('connecting');
    let url: string;
    if (isYoonee(this.cam)) {
      // The playlist hands off to a media origin that sends no CORS headers, so
      // only a native media load can follow it; hls.js would be blocked.
      if (!canPlayNativeHls(v)) { this.setStatus('unsupported'); return; }
      try { url = await cctvStreamUrl(this.cam.id); }
      catch { markCam(this.cam, 'offline'); this.setStatus('offline'); return; }
      if (this.gen !== g || !this.attached) return; // detached while handshaking
      this.tokenExp = cctvTokenExpiry();
    } else {
      url = this.urlForQuality(q);
    }
    this.currentUrl = url;
    if (canPlayNativeHls(v)) {
      this.native = true;
      v.src = url;
      this.bindNative();
      playSafe(v);
      return;
    }
    let Ctor: typeof Hls;
    try { Ctor = await ensureHls(); } catch { this.setStatus('error'); return; }
    if (this.gen !== g || !this.attached) return; // detached while loading hls.js
    if (!Ctor.isSupported()) { this.setStatus('error'); return; }
    const hls = new Ctor(gridConfig());
    this.hls = hls;
    hls.loadSource(url);
    hls.attachMedia(v);
    this.bindHls(Ctor, hls);
    this.applyCaps(q);
    playSafe(v);
  }

  private bindHls(Ctor: typeof Hls, hls: Hls): void {
    hls.on(Ctor.Events.FRAG_LOADED, () => { this.backoff = 1000; this.mediaErrs = 0; this.mfail = 0; });
    hls.on(Ctor.Events.ERROR, (_e, data) => {
      if (!data.fatal) return;
      const d = data.details || '';
      const code = data.response?.code;
      // YooNee token expired mid-stream -> fresh handshake, then reconnect.
      if (isYoonee(this.cam) && (code === 401 || code === 403)) { cctvInvalidate(); this.hardReinit(); return; }
      if (data.type === Ctor.ErrorTypes.NETWORK_ERROR) {
        // transient CORS/network blips must NOT flap the status; only the stall
        // watchdog (tick) sets "reconnecting" from real playback stalls.
        if (/manifestLoad/.test(d)) {
          this.mfail++;
          if (this.mfail >= 3 || code === 404 || code === 410) {
            markCam(this.cam, 'offline'); this.setStatus('offline');
          }
        }
        clearTimeout(this.bk);
        this.bk = window.setTimeout(() => this.hls?.startLoad(), this.backoff);
        this.backoff = Math.min(this.backoff * 2, 30000);
      } else if (data.type === Ctor.ErrorTypes.MEDIA_ERROR) {
        this.mediaErrs++;
        if (this.mediaErrs > 2) this.hardReinit();
        else { try { hls.recoverMediaError(); } catch { this.hardReinit(); } }
      } else this.hardReinit();
    });
    this.video.addEventListener('playing', () => { markCam(this.cam, 'live'); this.setStatus('live'); });
  }

  private bindNative(): void {
    this.video.addEventListener('playing', () => { this.nativeErrs = 0; markCam(this.cam, 'live'); this.setStatus('live'); });
    this.video.addEventListener('error', () => {
      if (!isYoonee(this.cam)) { markCam(this.cam, 'offline'); this.setStatus('offline'); return; }
      // Native HLS gives no status code. Back off across reinits (nativeErrs is NOT
      // reset by attach) so a persistent failure can't storm the session endpoint.
      this.nativeErrs++;
      // Don't invalidate the shared token on the way: a retired camera 404s no
      // matter the token, and churning it would re-handshake for every tile.
      if (this.nativeErrs >= 4) { markCam(this.cam, 'offline'); this.setStatus('offline'); return; }
      this.setStatus('reconnecting');
      clearTimeout(this.bk);
      this.bk = window.setTimeout(() => { if (this.el.isConnected && this.visible) this.hardReinit(); }, 1000 * this.nativeErrs);
    });
  }

  /** YooNee: swap in a URL with a fresh token without tearing down the decoder. */
  private async refreshYooneeToken(): Promise<void> {
    if (this.refreshing || !this.attached) return;
    this.refreshing = true;
    const g = this.gen;
    try {
      const url = await cctvStreamUrl(this.cam.id);
      if (this.gen !== g || !this.attached) return;
      this.currentUrl = url;
      this.tokenExp = cctvTokenExpiry();
      if (this.hls) this.hls.loadSource(url); // keeps the last frame painted
      else if (this.native) this.video.src = url;
    } catch { /* watchdog / error handler will reconnect */ }
    finally { this.refreshing = false; }
  }

  setStatus(s: TileStatus): void {
    this.el.dataset['s'] = s;
    const mtxt = $('.mtxt', this.el)!;
    const msub = $('.msub', this.el)!;
    const spin = $('.t-msg .spin', this.el) as HTMLElement;
    if (s === 'connecting') { mtxt.textContent = 'กำลังเชื่อมต่อ…'; msub.textContent = ''; spin.style.display = ''; }
    else if (s === 'offline') { spin.style.display = 'none'; mtxt.textContent = 'ออฟไลน์'; msub.textContent = this.cam.lastSeen ? `พบล่าสุด ${hms(this.cam.lastSeen)}` : 'ไม่มีสัญญาณสด'; this.ensureRetry(); }
    else if (s === 'error') { spin.style.display = 'none'; mtxt.textContent = 'เล่นไม่ได้'; this.ensureRetry(); }
    else if (s === 'unsupported') { spin.style.display = 'none'; mtxt.textContent = 'เบราว์เซอร์ไม่รองรับ'; msub.textContent = 'กล้องจราจรต้องใช้ Edge, Safari หรือ iOS'; }
    else if (s === 'idle') { spin.style.display = 'none'; mtxt.textContent = 'หยุดชั่วคราว'; msub.textContent = 'แตะเพื่อเล่น'; }
  }

  private ensureRetry(): void {
    if ($('.mbtn', this.el)) return;
    const b = document.createElement('button');
    b.className = 'mbtn';
    b.textContent = 'ลองใหม่';
    b.addEventListener('click', (e) => { e.stopPropagation(); this.hardReinit(); });
    $('.t-msg', this.el)!.appendChild(b);
  }

  private snapshot(): void {
    if (snapshotTo(this.video, this.poster)) this.poster.hidden = false;
  }

  detach(keepPoster = false): void {
    if (!this.attached) return;
    this.gen++;
    if (keepPoster) this.snapshot();
    clearTimeout(this.bk);
    if (this.hls) { try { this.hls.destroy(); } catch { /* noop */ } this.hls = null; }
    this.native = false;
    const v = this.video;
    try { v.pause(); v.removeAttribute('src'); v.load(); } catch { /* noop */ }
    this.attached = false;
    pool.attached.delete(this);
    this.setStatus('idle');
  }

  hardReinit(): void {
    this.detach(true);
    window.setTimeout(() => {
      if (this.el.isConnected && this.visible) void this.attach();
    }, 400);
  }

  /** Force this tile to (re)acquire a decoder even if the pool is at cap. */
  forcePlay(): void {
    this.lastVisible = Date.now();
    if (!this.attached) pool.reconcile();
  }

  tick(): void {
    if (!this.attached) return;
    // Proactively rotate the YooNee stream token ~15 s before it lapses.
    if (isYoonee(this.cam) && this.tokenExp && Date.now() > this.tokenExp - 15_000) void this.refreshYooneeToken();
    const v = this.video;
    if (v.paused) return;
    if (v.getVideoPlaybackQuality) {
      const g = v.getVideoPlaybackQuality();
      this.fps = Math.max(0, Math.round(g.totalVideoFrames - this.lastFrames));
      this.lastFrames = g.totalVideoFrames;
    }
    if (v.currentTime > 0 && v.currentTime !== this.lastT) {
      if (this.el.dataset['s'] !== 'live' && v.readyState >= 3) { markCam(this.cam, 'live'); this.setStatus('live'); }
      this.stalls = 0;
    } else if (v.readyState < 3 || v.currentTime === this.lastT) {
      this.stalls++;
      if (this.stalls === 3) { this.setStatus('reconnecting'); this.hls?.startLoad(); }
      else if (this.stalls >= 8) { this.stalls = 0; this.hardReinit(); return; }
    }
    this.lastT = v.currentTime;
    if (this.el.dataset['s'] === 'live') {
      const tele = $('.t-name', this.el)!;
      tele.textContent = labelOf(this.cam);
    }
  }
}
